import {
  Add,
  Apple,
  Delete,
  Link as LinkIcon,
  PhoneAndroid,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Button, Grid, IconButton, Paper, Tooltip } from "@mui/material";
import CustomTextField from "../../../../components/ui/Textfield";
import SectionHeader from "./SectionHeader";

const uid = () => Math.random().toString(36).slice(2, 9);

export interface AppLink {
  id: string;
  platform: "android" | "ios";
  url: string;
  label: string;
}

interface Props {
  appLinks: AppLink[];
  setAppLinks: React.Dispatch<React.SetStateAction<AppLink[]>>;
  primaryColor: string;
  secondaryColor: string;
  bgColor: string;
}

const AppLinksPanel: React.FC<Props> = ({
  appLinks,
  setAppLinks,
  primaryColor,
  secondaryColor,
  bgColor,
}) => {
  const addLink = () =>
    setAppLinks((prev) => [
      ...prev,
      { id: uid(), platform: "android", url: "", label: "" },
    ]);

  const updateLink = (id: string, field: keyof AppLink, value: string) =>
    setAppLinks((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );

  const removeLink = (id: string) =>
    setAppLinks((prev) => prev.filter((l) => l.id !== id));

  return (
    <Box>
      <SectionHeader title="App Download Links" color={primaryColor} />
      <AnimatePresence>
        {appLinks.map((link) => (
          <motion.div
            key={link.id}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Paper
              sx={{
                p: 2,
                mb: 2,
                boxShadow: 0,
                backgroundColor: bgColor,
                borderRadius: 2,
                border: `1px solid ${primaryColor}22`,
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {link.platform === "android" ? (
                      <PhoneAndroid
                        sx={{ color: secondaryColor, fontSize: 20 }}
                      />
                    ) : (
                      <Apple sx={{ color: secondaryColor, fontSize: 20 }} />
                    )}
                    <CustomTextField
                      select
                      fullWidth
                      size="small"
                      value={link.platform}
                      onChange={(e) =>
                        updateLink(link.id, "platform", e.target.value)
                      }
                      SelectProps={{ native: true }}
                    >
                      <option value="android">Android</option>
                      <option value="ios">iOS</option>
                    </CustomTextField>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextField
                    fullWidth
                    size="small"
                    placeholder="Button label"
                    value={link.label}
                    onChange={(e) =>
                      updateLink(link.id, "label", e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextField
                    fullWidth
                    size="small"
                    placeholder="https://play.google.com/..."
                    value={link.url}
                    onChange={(e) => updateLink(link.id, "url", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <LinkIcon
                          sx={{ color: primaryColor, mr: 1, fontSize: 16 }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => removeLink(link.id)}
                      sx={{ color: "error.main" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        ))}
      </AnimatePresence>
      <Button
        startIcon={<Add />}
        variant="outlined"
        size="small"
        onClick={addLink}
        sx={{
          borderColor: primaryColor,
          color: primaryColor,
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          "&:hover": { backgroundColor: `${primaryColor}12` },
        }}
      >
        Add App Link
      </Button>
    </Box>
  );
};

export default AppLinksPanel;
